import React, { useState, useCallback } from 'react';

const DCFView = () => {
  const [ticker, setTicker] = useState('');
  const [years, setYears] = useState('');
  const [projYears, setProjYears] = useState('');
  const [revenueGrowthY1, setRevenueGrowthY1] = useState('');
  const [revenueGrowthFinal, setRevenueGrowthFinal] = useState('');
  const [cogsMargin, setCogsMargin] = useState('');
  const [opexPct, setOpexPct] = useState('');
  const [depreciationPct, setDepreciationPct] = useState('');
  const [capexPct, setCapexPct] = useState('');
  const [dso, setDso] = useState('');
  const [dio, setDio] = useState('');
  const [dpo, setDpo] = useState('');
  const [taxRate, setTaxRate] = useState('');
  const [tvMethod, setTvMethod] = useState('gordon_growth');
  const [terminalGrowthRate, setTerminalGrowthRate] = useState('');
  const [exitMultiple, setExitMultiple] = useState('');
  const [multipleMetric, setMultipleMetric] = useState('ev/ebitda');
  const [sharesChangeRate, setSharesChangeRate] = useState('');
  const [terminalRevenueGrowth, setTerminalRevenueGrowth] = useState('');
  const [interestExpensePct, setInterestExpensePct] = useState('');
  const [dividendPayoutRatio, setDividendPayoutRatio] = useState('');
  const [netShareIssuancePctNi, setNetShareIssuancePctNi] = useState('');
  const [overrideWacc, setOverrideWacc] = useState('');

  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setOutput('');
    setError('');

    if (!ticker) {
      setError('Ticker is required.');
      setIsLoading(false);
      return;
    }

    let baseUrl = `http://127.0.0.1:8000/dcf/${ticker}`;
    const queryParams = [];

    if (taxRate) queryParams.push(`tax_rate=${encodeURIComponent(taxRate)}`);
    if (revenueGrowthY1) queryParams.push(`revenue_growth_y1=${encodeURIComponent(revenueGrowthY1)}`);
    if (revenueGrowthFinal) queryParams.push(`revenue_growth_final=${encodeURIComponent(revenueGrowthFinal)}`);
    if (cogsMargin) queryParams.push(`cogs_margin=${encodeURIComponent(cogsMargin)}`);
    if (opexPct) queryParams.push(`opex_pct=${encodeURIComponent(opexPct)}`);
    if (depreciationPct) queryParams.push(`depreciation_pct=${encodeURIComponent(depreciationPct)}`);
    if (capexPct) queryParams.push(`capex_pct=${encodeURIComponent(capexPct)}`);
    if (dso) queryParams.push(`dso=${encodeURIComponent(dso)}`);
    if (dio) queryParams.push(`dio=${encodeURIComponent(dio)}`);
    if (dpo) queryParams.push(`dpo=${encodeURIComponent(dpo)}`);
    if (tvMethod) queryParams.push(`tv_method=${encodeURIComponent(tvMethod)}`);
    if (terminalGrowthRate) queryParams.push(`terminal_growth_rate=${encodeURIComponent(terminalGrowthRate)}`);
    if (exitMultiple) queryParams.push(`exit_multiple=${encodeURIComponent(exitMultiple)}`);
    if (multipleMetric) queryParams.push(`exit_multiple_metric=${encodeURIComponent(multipleMetric)}`);
    if (sharesChangeRate) queryParams.push(`shares_change_rate=${encodeURIComponent(sharesChangeRate)}`);
    if (interestExpensePct) queryParams.push(`interest_expense_pct=${encodeURIComponent(interestExpensePct)}`);
    if (dividendPayoutRatio) queryParams.push(`dividend_payout_ratio=${encodeURIComponent(dividendPayoutRatio)}`);
    if (netShareIssuancePctNi) queryParams.push(`net_share_issuance_pct_ni=${encodeURIComponent(netShareIssuancePctNi)}`);
    if (overrideWacc) queryParams.push(`override_wacc=${encodeURIComponent(overrideWacc)}`);
    if (years) queryParams.push(`hist_years=${encodeURIComponent(years)}`);
    if (projYears) queryParams.push(`proj_years=${encodeURIComponent(projYears)}`);

    if (queryParams.length > 0) {
      baseUrl += `?${queryParams.join('&')}`;
    }

    console.log('Fetching URL:', baseUrl);

    try {
      const response = await fetch(baseUrl);
      console.log('Response:', response);
      const responseData = await response.text();
      console.log('Response Data:', responseData);
      
      if (!response.ok) {
        let errorMessage = responseData;
        try {
            const errorJson = JSON.parse(responseData);
            errorMessage = errorJson.detail || JSON.stringify(errorJson); 
        } catch (parseError) {
            // Keep responseData as is if not JSON
        }
        throw new Error(`HTTP error ${response.status}: ${errorMessage}`);
      }
      
      try {
        const jsonOutput = JSON.parse(responseData);
        setOutput(JSON.stringify(jsonOutput, null, 2));
      } catch (e) {
        setOutput(responseData);
      }

    } catch (err) {
      console.error('Error fetching DCF data:', err);
      setError(`Failed to fetch DCF data: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [
    ticker, years, projYears, revenueGrowthY1, revenueGrowthFinal, cogsMargin, opexPct, 
    depreciationPct, capexPct, dso, dio, dpo, taxRate, tvMethod, terminalGrowthRate, 
    exitMultiple, multipleMetric, sharesChangeRate, interestExpensePct, 
    dividendPayoutRatio, netShareIssuancePctNi, overrideWacc
  ]);

  const inputStyle = "mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm";
  const labelStyle = "block text-sm font-medium text-gray-700";

  return (
    <div className="p-6 bg-gray-50 h-full overflow-y-auto">
      <h2 className="text-2xl font-semibold text-gray-800 mb-6">Discounted Cash Flow Model</h2>
      <form onSubmit={handleSubmit} className="space-y-6">
        
        {/* Ticker (Required) */}
        <div>
          <label htmlFor="ticker" className={labelStyle}>Ticker <span className="text-red-500">*</span></label>
          <input type="text" id="ticker" value={ticker} onChange={(e) => setTicker(e.target.value)} className={inputStyle} required />
        </div>

        {/* Optional Parameters - Grouped for clarity */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <label htmlFor="years" className={labelStyle}>Projection Years (Total)</label>
            <input type="number" id="years" value={years} onChange={(e) => setYears(e.target.value)} className={inputStyle} placeholder="e.g., 10"/>
          </div>
          <div>
            <label htmlFor="projYears" className={labelStyle}>Detailed Projection Years</label>
            <input type="number" id="projYears" value={projYears} onChange={(e) => setProjYears(e.target.value)} className={inputStyle} placeholder="e.g., 5"/>
          </div>
          <div>
            <label htmlFor="revenueGrowthY1" className={labelStyle}>Revenue Growth Y1 (%)</label>
            <input type="number" step="0.01" id="revenueGrowthY1" value={revenueGrowthY1} onChange={(e) => setRevenueGrowthY1(e.target.value)} className={inputStyle} placeholder="e.g., 0.10 for 10%"/>
          </div>
          <div>
            <label htmlFor="revenueGrowthFinal" className={labelStyle}>Revenue Growth Final Year (%)</label>
            <input type="number" step="0.01" id="revenueGrowthFinal" value={revenueGrowthFinal} onChange={(e) => setRevenueGrowthFinal(e.target.value)} className={inputStyle} placeholder="e.g., 0.05 for 5%"/>
          </div>
          <div>
            <label htmlFor="cogsMargin" className={labelStyle}>COGS Margin (%)</label>
            <input type="number" step="0.01" id="cogsMargin" value={cogsMargin} onChange={(e) => setCogsMargin(e.target.value)} className={inputStyle} placeholder="e.g., 0.60 for 60%"/>
          </div>
          <div>
            <label htmlFor="opexPct" className={labelStyle}>Operating Expenses % of Revenue</label>
            <input type="number" step="0.01" id="opexPct" value={opexPct} onChange={(e) => setOpexPct(e.target.value)} className={inputStyle} placeholder="e.g., 0.20 for 20%"/>
          </div>
          <div>
            <label htmlFor="depreciationPct" className={labelStyle}>Depreciation % of Revenue</label>
            <input type="number" step="0.01" id="depreciationPct" value={depreciationPct} onChange={(e) => setDepreciationPct(e.target.value)} className={inputStyle} placeholder="e.g., 0.05 for 5%"/>
          </div>
          <div>
            <label htmlFor="capexPct" className={labelStyle}>Capital Expenditures % of Revenue</label>
            <input type="number" step="0.01" id="capexPct" value={capexPct} onChange={(e) => setCapexPct(e.target.value)} className={inputStyle} placeholder="e.g., 0.07 for 7%"/>
          </div>
          <div>
            <label htmlFor="dso" className={labelStyle}>Days Sales Outstanding (DSO)</label>
            <input type="number" id="dso" value={dso} onChange={(e) => setDso(e.target.value)} className={inputStyle} placeholder="e.g., 45"/>
          </div>
          <div>
            <label htmlFor="dio" className={labelStyle}>Days Inventory Outstanding (DIO)</label>
            <input type="number" id="dio" value={dio} onChange={(e) => setDio(e.target.value)} className={inputStyle} placeholder="e.g., 30"/>
          </div>
          <div>
            <label htmlFor="dpo" className={labelStyle}>Days Payables Outstanding (DPO)</label>
            <input type="number" id="dpo" value={dpo} onChange={(e) => setDpo(e.target.value)} className={inputStyle} placeholder="e.g., 60"/>
          </div>
          <div>
            <label htmlFor="taxRate" className={labelStyle}>Effective Tax Rate (%)</label>
            <input type="number" step="0.01" id="taxRate" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} className={inputStyle} placeholder="e.g., 0.21 for 21%"/>
          </div>
          <div>
            <label htmlFor="tvMethod" className={labelStyle}>Terminal Value Method</label>
            <select id="tvMethod" value={tvMethod} onChange={(e) => setTvMethod(e.target.value)} className={inputStyle}>
              <option value="gordon_growth">Gordon Growth Model</option>
              <option value="exit_multiple">Exit Multiple</option>
            </select>
          </div>
          <div>
            <label htmlFor="terminalGrowthRate" className={labelStyle}>Terminal Growth Rate (for Gordon Growth) (%)</label>
            <input type="number" step="0.01" id="terminalGrowthRate" value={terminalGrowthRate} onChange={(e) => setTerminalGrowthRate(e.target.value)} className={inputStyle} placeholder="e.g., 0.02 for 2%"/>
          </div>
          <div>
            <label htmlFor="exitMultiple" className={labelStyle}>Exit Multiple (for Exit Multiple Method)</label>
            <input type="number" step="0.1" id="exitMultiple" value={exitMultiple} onChange={(e) => setExitMultiple(e.target.value)} className={inputStyle} placeholder="e.g., 10.0"/>
          </div>
          <div>
            <label htmlFor="multipleMetric" className={labelStyle}>Multiple Metric (for Exit Multiple Method)</label>
            <select id="multipleMetric" value={multipleMetric} onChange={(e) => setMultipleMetric(e.target.value)} className={inputStyle}>
              <option value="ev/ebitda">EV/EBITDA</option>
              <option value="ev/ebit">EV/EBIT</option>
              <option value="ev/sales">EV/Sales</option>
              <option value="ps">P/S</option>
              <option value="pe">P/E</option>
              <option value="pb">P/B</option>
            </select>
          </div>
           <div>
            <label htmlFor="sharesChangeRate" className={labelStyle}>Shares Change Rate (% p.a.)</label>
            <input type="number" step="0.01" id="sharesChangeRate" value={sharesChangeRate} onChange={(e) => setSharesChangeRate(e.target.value)} className={inputStyle} placeholder="e.g., -0.01 for -1%"/>
          </div>
          <div>
            <label htmlFor="terminalRevenueGrowth" className={labelStyle}>Terminal Revenue Growth (%)</label>
            <input type="number" step="0.01" id="terminalRevenueGrowth" value={terminalRevenueGrowth} onChange={(e) => setTerminalRevenueGrowth(e.target.value)} className={inputStyle} placeholder="e.g., 0.02 for 2%"/>
          </div>
          <div>
            <label htmlFor="interestExpensePct" className={labelStyle}>Interest Expense % of Revenue</label>
            <input type="number" step="0.01" id="interestExpensePct" value={interestExpensePct} onChange={(e) => setInterestExpensePct(e.target.value)} className={inputStyle} placeholder="e.g., 0.01 for 1%"/>
          </div>
          <div>
            <label htmlFor="dividendPayoutRatio" className={labelStyle}>Dividend Payout Ratio (%)</label>
            <input type="number" step="0.01" id="dividendPayoutRatio" value={dividendPayoutRatio} onChange={(e) => setDividendPayoutRatio(e.target.value)} className={inputStyle} placeholder="e.g., 0.30 for 30%"/>
          </div>
          <div>
            <label htmlFor="netShareIssuancePctNi" className={labelStyle}>Net Share Issuance % of Net Income</label>
            <input type="number" step="0.01" id="netShareIssuancePctNi" value={netShareIssuancePctNi} onChange={(e) => setNetShareIssuancePctNi(e.target.value)} className={inputStyle} placeholder="e.g., 0.005 for 0.5%"/>
          </div>
          <div>
            <label htmlFor="overrideWacc" className={labelStyle}>Override WACC (%)</label>
            <input type="number" step="0.001" id="overrideWacc" value={overrideWacc} onChange={(e) => setOverrideWacc(e.target.value)} className={inputStyle} placeholder="e.g., 0.085 for 8.5%"/>
          </div>
        </div>

        <button 
          type="submit" 
          className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          disabled={isLoading || !ticker}
        >
          {isLoading ? 'Calculating...' : 'Run DCF Analysis'}
        </button>
      </form>

      {error && (
        <div className="mt-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
          <p className="font-semibold">Error:</p>
          <p>{error}</p>
        </div>
      )}

      {output && (
        <div className="mt-6 p-4 bg-gray-100 border border-gray-300 rounded-md">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">DCF Output:</h3>
          <pre className="whitespace-pre-wrap text-sm text-gray-600 bg-white p-3 rounded shadow-sm overflow-x-auto">{output}</pre>
        </div>
      )}
    </div>
  );
};

export default DCFView; 